import type { SatelliteWaterQuality } from "@/lib/conditions/satellite-water-display";
import {
  classifySecchi,
  secchiBadgeConfig,
  secchiBadgeText,
} from "@/lib/conditions/satellite-water-display";

export function WaterQualityBadge({
  data,
}: {
  data: SatelliteWaterQuality;
}) {
  if (data.secchiDepthM === null) return null;

  const label = classifySecchi(data.secchiDepthM);
  const config = secchiBadgeConfig(label);
  const text = secchiBadgeText(label);
  const depth = Math.round(data.secchiDepthM);

  const alerts: string[] = [];
  if (data.chlorophyllMgM3 !== null && data.chlorophyllMgM3 > 5) {
    alerts.push(
      "Elevated phytoplankton detected, possible algal bloom"
    );
  }
  if (data.spmGM3 !== null && data.spmGM3 > 10) {
    alerts.push(
      "High sediment levels, possible runoff or storm disturbance"
    );
  }

  return (
    <div
      className={`rounded-[14px] border p-4 md:p-5 ${config.bg} ${config.border}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className={`text-[13px] font-semibold ${config.text}`}>
            {text}: satellite Secchi estimate ~{depth} m
          </p>
          <p className="mt-1 text-[12px] text-text-muted">Optical water-clarity estimate, not measured underwater visibility or a bathing-water safety test.</p>
          <p className="mt-1 text-[11px] text-text-muted">Observation: {/^\d{4}-\d{2}-\d{2}$/.test(data.date) && Number.isFinite(Date.parse(data.date)) ? new Date(`${data.date}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }) : "date unavailable"}. Source: {data.dataSource || "unavailable"}.</p>
          {alerts.map((alert) => (
            <p
              key={alert}
              className="text-[12px] text-amber-700 mt-1.5"
            >
              {alert}
            </p>
          ))}
        </div>
        <div className="group relative shrink-0">
          <span
            tabIndex={0}
            className="inline-flex items-center justify-center min-w-11 min-h-11 rounded-full bg-white/60 text-[11px] text-text-muted cursor-help"
            aria-label="Water quality data source"
          >
            &#8505;
          </span>
          <div className="absolute right-0 top-7 w-56 bg-white rounded-[10px] border border-border-light shadow-lg p-3 text-[11px] text-text-muted leading-[1.6] opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-opacity z-20">
            Satellite water quality from Copernicus Marine (primary) and
            NOAA VIIRS (fallback). Updated daily. Accuracy varies by location.
          </div>
        </div>
      </div>
    </div>
  );
}
